import React, { useState, useEffect, useMemo } from "react";
import { Provider } from "react-redux";
import { Router } from "react-router";
import { createBrowserHistory } from "history";
import { fromJS } from "immutable";
import Routes from "./routes";
import createStore from "./redux/create";
import ReduxIntl from "~/intl/ReduxIntl";
import { changeLocale } from "~/redux/modules/intl";
import GlobalLoader from "./components/GlobalLoader";
import query from "query-string";
import { SessionDataProvider, useSessionData } from "./utils/session";
import constants from "./utils/constants";
import { initialState as appInitialState } from "./containers/App/reducer";
import ErrorScreen from "./containers/ErrorScreen/ErrorScreen";
import { getFromLocalStorage, saveToLocalStorage } from "./utils/misc";
import { CartAbandonmentContextProvider } from "./contexts/cart-abandonment/application/CartAbandonmentContext";
const Loading = () => <GlobalLoader />;

const compatibleInitialState = {};
Object.keys(window.__data).map(key => (compatibleInitialState[key] = fromJS(window.__data[key])));

function fireTracking() {
  if (!window.ga) {
    return;
  }
  window.ga("tgaown.send", "pageview", document.location.pathname + document.location.search);
  if (!window.wbkGaClientList) {
    return;
  }

  window.wbkGaClientList.forEach(gaClientName =>
    window.ga(
      `${gaClientName}.send`,
      "pageview",
      document.location.pathname + document.location.search
    )
  );
}

const history = createBrowserHistory();
let locale = window.navigator.language || window.navigator.userLanguage;
locale = locale && locale.substr(0, 2);

function mapSessionToInitialState(session) {
  if (!session) {
    return {};
  }
  return {
    app: fromJS({
      restoredSession: session,
    }).merge(appInitialState),
  };
}

function MainApp() {
  const [loaded, setLoaded] = useState(false);
  //This maps the session values to the initial redux state (makes integration easier)
  const session = useSessionData(null);
  const initialSessionState = useMemo(() => mapSessionToInitialState(session), [session]);
  const store = useMemo(
    () => createStore(null, { ...compatibleInitialState, ...initialSessionState }),
    [initialSessionState, compatibleInitialState]
  );
  useEffect(() => {
    const load = async () => {
      const actualLocale = session && session.locale ? session.locale : locale;
      await store.dispatch(changeLocale(actualLocale));
      setLoaded(true);
    };
    load();
  }, [locale]);

  const propertyTicker = useMemo(() => {
    const pathname = window.location.pathname;
    const firstSlashIndex = pathname?.indexOf("/");
    return pathname?.substring(firstSlashIndex + 1, pathname?.indexOf("/", firstSlashIndex + 1));
  }, []);

  return (
    <>
      {!loaded && <Loading />}
      {loaded && (
        <Provider store={store} key="provider">
          <ReduxIntl>
            <CartAbandonmentContextProvider session={session} propertyTicker={propertyTicker}>
              <Router onUpdate={fireTracking} history={history}>
                <Routes />
              </Router>
            </CartAbandonmentContextProvider>
          </ReduxIntl>
        </Provider>
      )}
    </>
  );
}

export default () => {
  const store = useMemo(() => createStore(null, { ...compatibleInitialState }), [
    compatibleInitialState,
  ]);
  //Check if we have an error code
  if (window.__additionalData.ERROR_CODE) {
    return (
      <Provider store={store} key="provider">
        <ReduxIntl>
          <ErrorScreen
            type={
              __additionalData.ERROR_CODE == 404
                ? constants.ERROR_SCREEN.NOT_FOUND
                : constants.ERROR_SCREEN.SERVER
            }
            showDesktopLogo={false}
            showMobileLogo={false}
          />
        </ReduxIntl>
      </Provider>
    );
  }
  //Load session if any in the url / session storage.
  //We need to do this so the session id may persist during the whole user session
  const session = useMemo(() => {
    const newSessionId = query.parse(window.location.search).session;
    const currentSessionId = getFromLocalStorage(constants.LOCAL_STORAGE_KEYS.SESSION_ID);
    if (newSessionId && (!currentSessionId || newSessionId !== currentSessionId)) {
      const currency = getFromLocalStorage(constants.LOCAL_STORAGE_KEYS.CURRENCY);
      //Delete browser storage that may be stale
      if (window.sessionStorage) {
        window.sessionStorage.clear();
      }
      if (window.localStorage) {
        window.localStorage.clear();
        // Persist currency after login
        saveToLocalStorage(constants.LOCAL_STORAGE_KEYS.CURRENCY, currency);
      }
    }
    return newSessionId || currentSessionId;
  }, []);

  return (
    <SessionDataProvider sessionId={session}>
      <MainApp />
    </SessionDataProvider>
  );
};
